// Create Team View Component
// Full-page view with Overview, Members, and Permissions tabs

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Save,
  X,
  Users,
  Shield,
  Code,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { TeamOverviewTab } from './detail/TeamOverviewTab';
import { TeamMembersTab } from './detail/TeamMembersTab';
import { TeamPermissionsTab } from './detail/TeamPermissionsTab';
import type { Team, User, Permission, TeamCreateDto } from '@/types/teams';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: Users,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface CreateTeamViewProps {
  teams: Team[];
  users: User[];
  permissions: Permission[];
  onBack: () => void;
  onCreate: (data: TeamCreateDto) => Promise<void>;
}

export function CreateTeamView({
  teams,
  users,
  permissions,
  onBack,
  onCreate,
}: Readonly<CreateTeamViewProps>) {
  const [activeTab, setActiveTab] = useState('overview');
  const [hasPermissionChanges, setHasPermissionChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // New team data
  const [newTeam, setNewTeam] = useState<Partial<Team>>({
    id: 'new-team',
    name: '',
    icon: 'users',
    color: '#3B82F6',
    description: '',
    isDefault: false,
    canDelete: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Selected members for the new team
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  // Selected permissions for the new team
  const [, setSelectedPermissions] = useState<string[]>([]);

  const IconComponent = TEAM_ICONS[newTeam.icon ?? 'users'] ?? Users;

  const handleSave = async () => {
    if (!newTeam.name?.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onCreate({
        name: newTeam.name.trim(),
        icon: newTeam.icon ?? 'users',
        color: newTeam.color ?? '#3B82F6',
        description: newTeam.description ?? '',
        // TODO: Add members and permissions to the create DTO
      });
      onBack();
    } catch (error) {
      console.error('Failed to create team:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // TODO: Show unsaved changes modal if there are changes
    onBack();
  };

  const handleUpdateTeam = (updates: Partial<Team>) => {
    setNewTeam(prev => ({ ...prev, ...updates }));
  };

  const handlePermissionsChange = (newPermissions: string[]) => {
    setSelectedPermissions(newPermissions);
    setHasPermissionChanges(true);
  };

  const isValid = newTeam.name?.trim();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6">
        {/* Team Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <div
            className="h-16 w-16 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${newTeam.color}20`, color: newTeam.color }}
          >
            <IconComponent className="h-8 w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold truncate">
                {newTeam.name ?? 'New Team'}
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedMemberIds.length} {selectedMemberIds.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasPermissionChanges && activeTab === 'permissions' && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
                Unsaved changes
              </span>
            )}
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Creating...' : 'Create Team'}
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Bar - Enhanced Enterprise Design */}
      <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
            <TabsTrigger 
              value="overview"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger 
              value="members"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Members
            </TabsTrigger>
            <TabsTrigger 
              value="permissions"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Permissions
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'overview' && (
          <TeamOverviewTab
            team={newTeam as Team}
            isEditMode={true}
            onUpdate={handleUpdateTeam}
            showMetadata={false}
          />
        )}
        
        {activeTab === 'members' && (
          <TeamMembersTab
            team={newTeam as Team}
            teams={teams}
            users={users}
            members={users.filter(u => selectedMemberIds.includes(u.id))}
            isEditMode={true}
          />
        )}
        
        {activeTab === 'permissions' && (
          <TeamPermissionsTab
            team={newTeam as Team}
            teams={teams}
            permissions={permissions}
            isEditMode={true}
            onPermissionsChange={handlePermissionsChange}
            onHasChanges={setHasPermissionChanges}
          />
        )}
      </div>
    </div>
  );
}
